import React, { useState, useEffect } from 'react';
import StartScreen from './StartScreen';
import GameScene from './GameScene';
import HUD from './HUD';
import CrashCard from './CrashCard';
import StatsModal from './StatsModal';
import MobileControls from './MobileControls';
import OrientationGuard from './OrientationGuard';

const isTouchDevice = () =>
  typeof window !== 'undefined' &&
  ('ontouchstart' in window || navigator.maxTouchPoints > 0);

const DrunkDrivingGame = () => {
  const [gameState, setGameState] = useState('start');
  const [drinkCount, setDrinkCount] = useState(0);
  const [gameStats, setGameStats] = useState({
    timeSurvived: 0,
    distanceTraveled: 0,
    crashes: 0,
    currentSpeed: 0
  });
  const [educationalFact, setEducationalFact] = useState(null);
  const [showStats, setShowStats] = useState(false);

  // Countdown state — game scene runs but is paused during these phases
  const [paused, setPaused] = useState(false);
  const [countdown, setCountdown] = useState(null); // null | 'loading' | 3 | 2 | 1 | 'GO'

  // Debug — ?debug=crash renders the crash card immediately with sample data
  // so designers can iterate on the result screen without dying in-game.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('debug') === 'crash') {
      setDrinkCount(3);
      setGameStats({ timeSurvived: 5.7, distanceTraveled: 278.9, crashes: 1, currentSpeed: 0 });
      setEducationalFact({
        fact:
          "On a highway at 80 km/h, a sober driver's vehicle will travel 22–33 meters after braking, whereas a drunk driver's vehicle will travel 55–88 meters after hitting the brakes.",
        source: 'Braking Distance Studies'
      });
      setGameState('crashed');
    }
  }, []);

  const handleStartGame = (selectedDrinks) => {
    setDrinkCount(selectedDrinks);
    setGameStats({
      timeSurvived: 0,
      distanceTraveled: 0,
      crashes: 0,
      currentSpeed: 0
    });
    setGameState('playing');
    setPaused(true);
    setCountdown('loading');
  };

  // Run the loading + countdown sequence once we enter playing
  useEffect(() => {
    if (gameState !== 'playing' || countdown === null) return;

    let timers = [];
    // Initial loading buffer so heavy GLB assets (BMW, palms) have time to decode
    timers.push(setTimeout(() => setCountdown(3), 1800));
    timers.push(setTimeout(() => setCountdown(2), 2800));
    timers.push(setTimeout(() => setCountdown(1), 3800));
    timers.push(setTimeout(() => setCountdown('GO'), 4800));
    timers.push(setTimeout(() => {
      setCountdown(null);
      setPaused(false);
    }, 5400));

    return () => timers.forEach((t) => clearTimeout(t));
  }, [gameState, countdown === null ? 'null' : 'set']);

  const handleCrash = async (finalStats) => {
    setGameStats(prev => ({ ...prev, ...finalStats, crashes: prev.crashes + 1 }));

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/educational-fact`);
      const data = await response.json();
      setEducationalFact(data);
    } catch (error) {
      console.error('Failed to fetch educational fact:', error);
      setEducationalFact({
        fact: 'Alcohol impairs your ability to drive safely. Never drink and drive.',
        source: null
      });
    }

    setGameState('crashed');

    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/game-sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          drink_count: drinkCount,
          time_survived: finalStats.timeSurvived,
          distance_traveled: finalStats.distanceTraveled,
          crashes: 1
        })
      });
    } catch (error) {
      console.error('Failed to save session:', error);
    }
  };

  const handleRestart = () => {
    setGameState('start');
    setEducationalFact(null);
    setCountdown(null);
    setPaused(false);
  };

  const handleContinue = () => {
    setGameState('playing');
    setEducationalFact(null);
    setPaused(true);
    setCountdown('loading');
  };

  const updateStats = (stats) => {
    setGameStats(prev => ({ ...prev, ...stats }));
  };

  return (
    <OrientationGuard>
      <div className="game-frame">
        {gameState === 'start' && (
          <StartScreen onStart={handleStartGame} onViewStats={() => setShowStats(true)} />
        )}

        {gameState === 'playing' && (
          <>
            <GameScene
              drinkCount={drinkCount}
              onCrash={handleCrash}
              onUpdateStats={updateStats}
              paused={paused}
            />
            <HUD
              speed={gameStats.currentSpeed}
              drinkCount={drinkCount}
              timeSurvived={gameStats.timeSurvived}
              distanceTraveled={gameStats.distanceTraveled}
            />
            {isTouchDevice() && <MobileControls />}
            {countdown !== null && (
              <CountdownOverlay value={countdown} />
            )}
          </>
        )}

        {gameState === 'crashed' && educationalFact && (
          <CrashCard
            fact={educationalFact}
            stats={gameStats}
            drinkCount={drinkCount}
            onRestart={handleRestart}
            onContinue={handleContinue}
          />
        )}

        {showStats && (
          <StatsModal onClose={() => setShowStats(false)} />
        )}
      </div>
    </OrientationGuard>
  );
};

// Loading / 3-2-1-GO overlay
const CountdownOverlay = ({ value }) => {
  const isLoading = value === 'loading';
  const isGo = value === 'GO';

  return (
    <div
      data-testid="countdown-overlay"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: isLoading ? 'rgba(0,0,0,0.85)' : 'rgba(0,0,0,0.25)',
        backdropFilter: isLoading ? 'blur(8px)' : 'blur(2px)',
        pointerEvents: 'none',
        transition: 'background 0.3s ease'
      }}
    >
      {isLoading ? (
        <>
          <div
            style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: '12px',
              letterSpacing: '0.3em',
              color: 'rgba(255,255,255,0.55)',
              marginBottom: '16px'
            }}
          >
            LOADING ASSETS
          </div>
          <div
            style={{
              fontFamily: 'Cabinet Grotesk, sans-serif',
              fontSize: '28px',
              fontWeight: 900,
              letterSpacing: '0.05em',
              color: '#fff'
            }}
          >
            PREPARING DRIVE
          </div>
          <div
            style={{
              marginTop: '24px',
              width: '180px',
              height: '2px',
              background: 'rgba(255,255,255,0.1)',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(90deg, transparent, #fff, transparent)',
                animation: 'loadingShine 1.2s ease-in-out infinite'
              }}
            />
          </div>
          <style>{`
            @keyframes loadingShine {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(100%); }
            }
            @keyframes countPulse {
              0% { transform: scale(0.6); opacity: 0; }
              30% { transform: scale(1.1); opacity: 1; }
              80% { transform: scale(1); opacity: 1; }
              100% { transform: scale(1.4); opacity: 0; }
            }
          `}</style>
        </>
      ) : (
        <div
          key={value}
          data-testid={`countdown-${value}`}
          style={{
            fontFamily: 'Cabinet Grotesk, sans-serif',
            fontSize: isGo ? '180px' : '220px',
            fontWeight: 900,
            letterSpacing: '-0.04em',
            color: isGo ? '#34d471' : '#fff',
            textShadow: isGo
              ? '0 0 40px rgba(52, 212, 113, 0.6)'
              : '0 0 30px rgba(255,255,255,0.3)',
            animation: 'countPulse 0.95s ease-out'
          }}
        >
          {value}
        </div>
      )}
    </div>
  );
};

export default DrunkDrivingGame;
